import { httpClient } from './client'
import configProvider from '../../config'

interface Address {
  numberAndStreet?: string
  city?: string
  province?: string
  postalCode?: string
  country?: string
}
export type SwtchAddress = Address

interface GoogleGeocodeLatLng {
  lat: number
  lng: number
}

interface GoogleGeocodeGeometry {
  location: GoogleGeocodeLatLng
}

interface GoogleGeocodeAddressComponent {
  long_name: string
  short_name: string
  types: string[]
}

interface GoogleGeocodeResult {
  geometry: GoogleGeocodeGeometry
  address_components: GoogleGeocodeAddressComponent[]
  formatted_address: string
  partial_match: boolean
  place_id: string
  types: string[]
}

interface GoogleGeocodeResponse {
  results: GoogleGeocodeResult[]
  status: string
  error_message?: string
}

const googleGeocodeBaseUrl = 'https://maps.googleapis.com/maps/api/geocode/json'

export type GeocodingSuccess = {
  found: true
  lat: number
  lng: number
  numberAndStreet?: string
  address?: string
  province?: string
  country?: string
  city?: string
  postalCode?: string
}

export type GeocodingFailure = {
  found: false
  code: string
  message: string
}

export type GeocodingResult = GeocodingSuccess | GeocodingFailure

const getLocationFromAddress = async (address: SwtchAddress): Promise<GeocodingResult> => {
  const requestUrl = buildGeocodeUrl(address)
  const response = await callGeocodeUrlV2(requestUrl)
  return handleGeocodingResponse(response, address)
}

const buildGeocodeUrl = (address: SwtchAddress): string => {
  const addressComponents = [
    address.numberAndStreet,
    address.city,
    address.province,
    address.postalCode,
    address.country,
  ]
  const urlEncodedComponents = addressComponents.map((c) => encodeURIComponent(c ?? '')).join(',+')
  return `${googleGeocodeBaseUrl}?address=${urlEncodedComponents}&key=${configProvider.config.googleMapsApiKey}`
}

const callGeocodeUrlV2 = async (url: string): Promise<GoogleGeocodeResponse> => {
  try {
    const response = await httpClient<GoogleGeocodeResponse>(url, {})
    return response
  } catch (error) {
    let errorMessage = 'An unknown error occurred.'

    if (error instanceof Error) {
      errorMessage = error.message
    }

    return { results: [], status: 'REQUEST_FAILED', error_message: errorMessage }
  }
}

const selectBestGeocodeResult = (response: GoogleGeocodeResponse, address: SwtchAddress) => {
  let result = response.results[0] // Default to the first result
  if (response.results.length > 1) {
    result = response.results.find((r) => r.formatted_address.includes(address.numberAndStreet ?? '')) || result
  }
  return result
}

const extractAddressComponent = (result: GoogleGeocodeResult, type: string): string | undefined => {
  return result.address_components.find((ac) => ac.types.includes(type))?.short_name
}

const formatNumberAndStreet = (result: GoogleGeocodeResult, country?: string) => {
  if (country === 'United States' || country === 'Canada') {
    return `${extractAddressComponent(result, 'street_number') || ''} ${
      extractAddressComponent(result, 'route') || ''
    }`.trim()
  }
  return result.formatted_address
    .replace(new RegExp(`, ${extractAddressComponent(result, 'postal_code')}`), '')
    .replace(new RegExp(`, ${extractAddressComponent(result, 'sublocality')}`), '')
    .replace(new RegExp(`, ${extractAddressComponent(result, 'locality')}`), '')
    .replace(new RegExp(`, ${extractAddressComponent(result, 'country')}`), '')
    .replace(new RegExp(`, ${extractAddressComponent(result, 'administrative_area_level_1')}`), '')
    .replace(/,\s*$/, '')
}

const extractCity = (result: GoogleGeocodeResult): string | undefined => {
  return (
    result.address_components.find((ac) => ac.types.includes('locality'))?.short_name ||
    result.address_components.find((ac) => ac.types.includes('neighborhood'))?.short_name ||
    result.address_components.find((ac) => ac.types.includes('sublocality'))?.short_name ||
    result.address_components.find((ac) => ac.types.includes('postal_town'))?.short_name
  )
}

const handleGeocodingResponse = (response: GoogleGeocodeResponse, address: SwtchAddress): GeocodingResult => {
  if (!response.results || response.results.length === 0) {
    return { found: false, message: 'No results found.', code: response.status }
  }

  let result = selectBestGeocodeResult(response, address)
  const { lat, lng } = result.geometry.location

  let country = extractAddressComponent(result, 'country')
  let province = extractAddressComponent(result, 'administrative_area_level_1')

  const numberAndStreet = formatNumberAndStreet(result, country)

  // Handle Puerto Rico case
  if (country === 'Puerto Rico' || country === 'PR') {
    country = 'US'
    province = 'PR'
  }

  return {
    found: true,
    lat,
    lng,
    numberAndStreet,
    city: extractCity(result),
    postalCode: extractAddressComponent(result, 'postal_code'),
    country,
    province,
  }
}

const geocode = async (address: SwtchAddress): Promise<GeocodingResult> => {
  const addressComponents: Array<string | undefined> = [
    address.numberAndStreet,
    address.city,
    address.province,
    address.postalCode,
    address.country,
  ]
  const urlEncodedComponents = addressComponents.map((c) => (c ? encodeURIComponent(c) : '')).join(',+')
  const requestUrl = `${googleGeocodeBaseUrl}?address=${urlEncodedComponents}&key=${configProvider.config.googleMapsApiKey}`

  return await callGeocodeUrl(requestUrl)
}

const reverseGeocode = async (latlng: { lat: number; lng: number }): Promise<GeocodingResult> => {
  return await callGeocodeUrl(
    `${googleGeocodeBaseUrl}?latlng=${latlng.lat},${latlng.lng}&key=${configProvider.config.googleMapsApiKey}`,
  )
}

const callGeocodeUrl = async (requestUrl: string): Promise<GeocodingResult> => {
  const res = await httpClient<GoogleGeocodeResponse>(requestUrl, {})
  if (res.results.length === 0) {
    return {
      found: false,
      code: res.status,
      message: res.error_message || 'Google says no',
    }
  } else {
    const latlng = res.results[0].geometry.location
    return {
      found: true,
      ...latlng,
      province: res.results[0].address_components.find((ac) => ac.types.includes('administrative_area_level_1'))
        ?.short_name,
      country: res.results[0].address_components.find((ac) => ac.types.includes('country'))?.short_name,
    }
  }
}

export { geocode, reverseGeocode, getLocationFromAddress }
